import React, { useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Chip,
    Stack,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Paper,
    Tooltip,
    Button,
    Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

/**
 * Redesigned, multi-line version of the DndHistoryModal.
 * - Uses MUI <Dialog /> for accessibility (focus trap, ESC to close)
 * - Each entry is displayed as an Accordion (multi-line) with grouped fields
 * - Status chips for CCM/RPM/BHI; details (date + reason) in 3 cards
 * - Notes/Legacy Notes shown in their own blocks with copy buttons
 * - Entries sorted by createdon (newest first)
 *
 * Props:
 *   isOpen: boolean
 *   onClose: function
 *   patientHistory: array of history rows (same shape as your current data)
 */
const DndHistoryModal = ({ isOpen, onClose, patientHistory = [] }) => {
    const [expandedIndex, setExpandedIndex] = useState(0);

    const sortedHistory = useMemo(() => {
        return [...patientHistory].sort((a, b) => {
            const da = a?.createdon ? new Date(a.createdon).getTime() : 0;
            const db = b?.createdon ? new Date(b.createdon).getTime() : 0;
            return db - da; // newest first
        });
    }, [patientHistory]);

    const patient = sortedHistory?.[0];

    const formatDateTime = (val) =>
        val
            ? new Date(val).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            })
            : "—";

    const chipProps = (value) => {
        const raw = (value === undefined || value === null || value === '') ? 'N/A' : String(value);
        const lower = raw.toLowerCase();
        if (lower === "yes") return { color: "error", label: "Yes" };
        if (lower === "no") return { color: "success", label: "No" };
        if (lower === "no selection") return { color: "warning", label: "No Selection" };
        return { color: "default", label: raw === "N/A" ? "N/A" : raw };
    };

    const SummaryChips = ({ entry }) => {
        const useCalculated = ((entry?.source_system || '').toLowerCase() !== 'tapestry opt out application'.toLowerCase());
        const ccmVal = useCalculated ? entry?.calculatedccm : entry?.ccm;
        const rpmVal = useCalculated ? entry?.calculatedrpm : entry?.rpm;
        const bhiVal = useCalculated ? entry?.calculatedbhi : entry?.bhi;

        return (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" variant="filled" {...chipProps(ccmVal)} label={`CCM: ${chipProps(ccmVal).label}`} />
                <Chip size="small" variant="filled" {...chipProps(rpmVal)} label={`RPM: ${chipProps(rpmVal).label}`} />
                <Chip size="small" variant="filled" {...chipProps(bhiVal)} label={`BHI: ${chipProps(bhiVal).label}`} />
            </Stack>
        );
    };

    const DetailCard = ({ title, date, reason }) => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.9 }}>
                {title}
            </Typography>
            <Typography variant="body2"><strong>Date:</strong> {formatDateTime(date)}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Reason:</strong> {reason || "—"}
            </Typography>
        </Paper>
    );

    const NotesBlock = ({ label, text }) => (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>{label}</Typography>
                <Tooltip title={text ? "Copy" : "Nothing to copy"}>
          <span>
            <IconButton size="small" onClick={() => text && navigator.clipboard.writeText(text)} disabled={!text}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
                </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{text || "—"}</Typography>
        </Paper>
    );

    return (
        <Dialog open={!!isOpen} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6">
                            Opt-out History{patient ? ` for ${patient.first_name} ${patient.last_name}` : ""}
                        </Typography>
                        {patient && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                Patient ID: {patient.patient_id} • {sortedHistory.length} entr{sortedHistory.length === 1 ? "y" : "ies"}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: (t) => (t.palette.mode === "light" ? "#fafafa" : t.palette.background.default) }}>
                {sortedHistory.length === 0 ? (
                    <Typography variant="body1">No history available.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {sortedHistory.map((entry, idx) => (
                            <Accordion key={idx} expanded={expandedIndex === idx} onChange={() => setExpandedIndex(expandedIndex === idx ? -1 : idx)} sx={{ borderRadius: 2, overflow: "hidden" }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={3}>
                                            <Typography variant="subtitle2">{formatDateTime(entry.createdon)}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Created by: {entry.created_by || "—"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                Source: {entry.source_system || "—"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <SummaryChips entry={entry} />
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <DetailCard title="CCM" date={entry.ccm_date} reason={entry.ccm_reason} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DetailCard title="RPM" date={entry.rpm_date} reason={entry.rpm_reason} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DetailCard title="BHI" date={entry.bhi_date} reason={entry.bhi_reason} />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <NotesBlock label="Notes" text={entry.notes_free_text} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <NotesBlock label="Legacy Notes" text={entry.reason} />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions>
                <Button variant="contained" color="secondary" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DndHistoryModal;
